/** Thin fetch wrapper. Cookies ride along automatically (same origin). */
import { pulse } from "./netPulse.js";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
  }
}

/** Browsers refuse a keepalive request whose body would take the page's
 *  in-flight keepalive total past 64 KiB; stay well under it. */
const KEEPALIVE_MAX = 32_000;

/** A read gives up after this, so a stuck one rejects instead of piling up
 *  under the next poll's. A route that legitimately takes longer (a git read,
 *  `du`) passes its own `timeoutMs`. */
export const READ_TIMEOUT_MS = 20_000;
/** Writes wait longer: starting a Claude or a run waits on the bridge for up to
 *  three minutes (CLAUDE_TIMEOUT_MS, server/tmux.ts), and giving up would not
 *  stop the server doing it — only say it failed when it may not have. */
/** A read that runs git on the bridge (a graph, the schedule's graphs), which
 *  gives git a minute (GIT_TIMEOUT_MS, server/tmux.ts). */
export const GIT_READ_TIMEOUT_MS = 90_000;
export const WRITE_TIMEOUT_MS = 4 * 60_000;

export interface RequestOptions {
  /** Give up after this long (an `ApiError` with status 0); 0 never does. */
  timeoutMs?: number;
}

/** `fetch`, given up on after `timeoutMs`. */
async function timed(path: string, init: RequestInit, timeoutMs: number): Promise<{ res: Response; text: string }> {
  const abort = timeoutMs > 0 ? new AbortController() : null;
  const timer = abort ? setTimeout(() => abort.abort(), timeoutMs) : null;
  try {
    const res = await fetch(path, abort ? { ...init, signal: abort.signal } : init);
    return { res, text: await res.text() };
  } catch (err) {
    if (abort?.signal.aborted) throw new ApiError(`No answer from the server in ${Math.round(timeoutMs / 1000)}s`, 0, null);
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const json = body === undefined ? undefined : JSON.stringify(body);
  const { res, text } = await timed(
    path,
    {
      method,
      headers: json === undefined ? {} : { "content-type": "application/json" },
      body: json,
      // A field saved as the page is left (back out of a deep link) must not be
      // cancelled with the page. Writes only, and only small ones.
      keepalive: method !== "GET" && (json?.length ?? 0) < KEEPALIVE_MAX,
    },
    opts.timeoutMs ?? (method === "GET" ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS),
  );
  pulse();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message = (parsed as { error?: string } | null)?.error ?? res.statusText;
    throw new ApiError(message, res.status, parsed);
  }
  return parsed as T;
}

/** A file's bytes as the body, as they are: JSON would mean base64 in a string. */
async function upload<T>(path: string, file: Blob, opts: RequestOptions = {}): Promise<T> {
  // A big file on a slow link takes as long as it takes: no limit unless asked.
  const { res, text } = await timed(path, { method: "POST", headers: { "content-type": "application/octet-stream" }, body: file }, opts.timeoutMs ?? 0);
  pulse();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // A proxy's own refusal (too large, say) is a page, not JSON.
  }
  if (!res.ok) throw new ApiError((parsed as { error?: string } | null)?.error ?? res.statusText, res.status, parsed);
  return parsed as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions): Promise<T> => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> => request<T>("POST", path, body ?? {}, opts),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> => request<T>("PATCH", path, body, opts),
  put: <T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> => request<T>("PUT", path, body, opts),
  del: <T>(path: string, opts?: RequestOptions): Promise<T> => request<T>("DELETE", path, undefined, opts),
  upload,
};
